import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Mail, Phone, MessageCircle, RefreshCw } from "lucide-react";
import Logo from "@/components/Logo";
import { CONTACT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ems-admin-token";
const STATUSES = [
  { key: "nouvelle", label: "Nouvelle" },
  { key: "contactee", label: "Contactée" },
  { key: "fermee", label: "Fermée" },
];
const STATUS_STYLES = {
  nouvelle: "bg-amber-100 text-amber-800",
  contactee: "bg-brandtint text-branddark",
  fermee: "bg-slate-200 text-slate-600",
};
const MATERIEL_LABELS = {
  smartphones: "Smartphones",
  composants: "Composants électroniques",
  mixtes: "Lots mixtes",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Connexion impossible. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });

  const loadRequests = async () => {
    setFetching(true);
    try {
      const { data } = await axios.get(`${API}/admin/requests`, {
        headers: authHeaders(),
      });
      setRequests(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Impossible de charger les demandes.");
      }
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (token) loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      toast.success("Bienvenue — connexion réussie.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setRequests([]);
    toast.success("Déconnecté.");
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/requests/${id}`, { status }, { headers: authHeaders() });
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch {
      toast.error("Mise à jour impossible.");
    }
  };

  const newCount = requests.filter((r) => r.status === "nouvelle").length;

  /* ---------------- LOGIN ---------------- */
  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-5 py-16">
        <div className="w-full max-w-md border border-line bg-white p-8 shadow-card sm:p-10">
          <div className="flex justify-center">
            <Logo className="h-12 w-auto" />
          </div>
          <h1 className="mt-8 text-center font-display text-2xl font-bold tracking-[-0.02em] text-ink">
            Espace privé
          </h1>
          <p className="mt-2 text-center text-sm font-medium text-ink2">
            Suivi des demandes de lots — accès administrateur
          </p>
          <form onSubmit={onLogin} noValidate className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="mb-2 block font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-ink2">
                Email
              </label>
              <input
                className="field"
                type="email"
                data-testid="admin-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@ecomobilesolution.fr"
              />
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-ink2">
                Mot de passe
              </label>
              <input
                className="field"
                type="password"
                data-testid="admin-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
              />
            </div>
            {error && (
              <p className="border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="admin-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="w-full bg-brand px-8 py-4 font-display text-[13px] font-bold uppercase tracking-[0.12em] text-white transition-colors duration-200 hover:bg-branddark disabled:opacity-60"
            >
              {loading ? "Connexion…" : "Se connecter"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  /* ---------------- DASHBOARD ---------------- */
  return (
    <div className="min-h-screen bg-paper pb-20">
      <header className="border-b border-line bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Logo className="h-9 w-auto" />
          <div className="flex items-center gap-3">
            <button
              onClick={loadRequests}
              data-testid="admin-refresh"
              className="flex items-center gap-2 border border-line bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] text-ink2 transition-colors hover:border-brand hover:text-brand"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${fetching ? "animate-spin" : ""}`} />
              Actualiser
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 border border-ink/20 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] text-ink transition-colors hover:border-red-400 hover:text-red-600"
            >
              <LogOut className="h-3.5 w-3.5" />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 pt-10 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-[-0.02em] text-ink">
              Demandes de lots
            </h1>
            <p className="mt-2 text-sm font-medium text-ink2">
              {requests.length} demande{requests.length > 1 ? "s" : ""} au total —{" "}
              {newCount} nouvelle{newCount > 1 ? "s" : ""} à traiter
            </p>
          </div>
        </div>

        {fetching && requests.length === 0 ? (
          <p className="mt-16 text-center text-sm font-medium text-ink3">Chargement…</p>
        ) : requests.length === 0 ? (
          <div className="mt-16 border border-line bg-white p-12 text-center" data-testid="admin-empty">
            <p className="font-display text-xl font-bold text-ink">Aucune demande pour le moment</p>
            <p className="mt-2 text-sm font-medium text-ink2">
              Les demandes envoyées depuis le formulaire de contact apparaîtront ici.
            </p>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {requests.map((r) => (
              <div key={r.id} className="border border-line bg-white p-6 shadow-sm" data-testid={`admin-request-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${
                          STATUS_STYLES[r.status] || STATUS_STYLES.nouvelle
                        }`}
                        data-testid={`request-status-${r.id}`}
                      >
                        {STATUSES.find((s) => s.key === r.status)?.label || r.status}
                      </span>
                      <span className="font-mono text-[11px] text-ink3">
                        {new Date(r.created_at).toLocaleString("fr-FR")}
                      </span>
                    </div>
                    <p className="mt-3 font-display text-xl font-bold tracking-[-0.01em] text-ink">
                      {r.entreprise}
                    </p>
                    <p className="mt-1 text-sm font-medium text-ink2">{r.nom}</p>
                  </div>
                  <select
                    value={r.status}
                    data-testid={`request-status-select-${r.id}`}
                    onChange={(e) => setStatus(r.id, e.target.value)}
                    className="border border-line bg-paper px-3 py-2 text-xs font-bold uppercase tracking-wider text-ink"
                  >
                    {STATUSES.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-4 flex flex-wrap gap-4 border-t border-line pt-4 text-sm font-medium">
                  <a
                    href={`mailto:${r.email}`}
                    className="flex items-center gap-2 text-ink2 transition-colors hover:text-brand"
                    data-testid={`request-email-${r.id}`}
                  >
                    <Mail className="h-4 w-4 text-brand" />
                    {r.email}
                  </a>
                  {r.phone && (
                    <>
                      <a
                        href={`tel:${r.phone}`}
                        className="flex items-center gap-2 text-ink2 transition-colors hover:text-brand"
                        data-testid={`request-phone-${r.id}`}
                      >
                        <Phone className="h-4 w-4 text-brand" />
                        {r.phone}
                      </a>
                      <a
                        href={`https://wa.me/${r.phone.replace(/[^0-9]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-ink2 transition-colors hover:text-brand"
                      >
                        <MessageCircle className="h-4 w-4 text-brand" />
                        WhatsApp
                      </a>
                    </>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="border border-line bg-paper px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-ink2">
                    {MATERIEL_LABELS[r.materiel] || r.materiel}
                  </span>
                  {r.quantite && (
                    <span className="border border-line bg-paper px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-ink2">
                      {r.quantite}
                    </span>
                  )}
                </div>

                <p
                  className={`mt-4 cursor-pointer text-sm leading-relaxed text-ink2 ${expanded === r.id ? "" : "line-clamp-2"}`}
                  onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  data-testid={`request-description-${r.id}`}
                >
                  {r.description}
                </p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
